/**
 * AMS 申报凭证
 *
 * 设计来源：设计文档 §7.2.1（AMS）/ §7.3（区块：基本信息 / 货物汇总 / 集装箱 / 日志）
 * - 箱行铁律 D8：QTY / CBM / WEIGHT 取该箱匹配货物行原值（AMS 报文约束 1 箱 : 1 货行）
 * - 仅汇总区块对 cargoList 求和
 */
import type { CertificateBuilder, CertificateContext, CertificateData, CertificateSection } from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  cargoRowsForContainer,
  dedupeMissing,
  dictPort,
  filingStatus,
  formatDateTime,
  gridSection,
  latestReceipt,
  logsSection,
  pick,
  receiptValue,
  rowsOf,
  sumOf,
  tableSection,
  text,
} from './shared'

export const buildAmsCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add(
      'FILING STATUS',
      filingStatus(
        pick(receipt, 'receiptStatus') || pick(detail, 'receiptStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add(
      'TRANSMITTED(ET)',
      formatDateTime(pick(detail, 'sendTime') || pick(receipt, 'sendTime'))
    )
    .add('HOUSE CARGO CONTROL NUMBER', pick(detail, 'hblNo'))
    .add('PRIMARY CARGO CONTROL NUMBER', pick(detail, 'mblNo'))
    .add('VESSEL', pick(detail, 'vesselName'))
    .add('VOYAGE', pick(detail, 'voyage'))
    .add('PORT OF LOADING', dictPort(dict, detail.loadingPortId))
    .add('PORT OF DISCHARGE', dictPort(dict, detail.dischargePortId))
    .add('ETD', formatDateTime(pick(detail, 'etd')))
    .add('ETA', formatDateTime(pick(detail, 'eta')))

  const cargoList = rowsOf(detail, 'cargoList')
  const totalFields = [
    { label: 'TOTAL QTY', value: sumOf(cargoList, 'quantity') },
    { label: 'TOTAL CBM', value: sumOf(cargoList, 'volume') },
    { label: 'TOTAL WEIGHT', value: sumOf(cargoList, 'grossWeight') },
  ].map(field => {
    if (!field.value) missing.push(field.label)
    return { label: field.label, value: field.value || DASH }
  })

  const containerRows = rowsOf(detail, 'containerList').map(row => {
    const cargo = cargoRowsForContainer(detail, text(row.containerNo))[0]
    return [
      text(row.containerNo) || DASH,
      text(row.sealNo) || DASH,
      text(cargo?.quantity) || DASH,
      text(cargo?.volume) || DASH,
      text(cargo?.grossWeight) || DASH,
    ]
  })

  const sections: CertificateSection[] = [
    gridSection(2, info.fields),
    gridSection(3, totalFields, 'CARGO INFORMATION'),
    tableSection(
      'CONTAINERS',
      ['CONTAINER NO.', 'SEAL NO', 'QTY', 'CBM', 'WEIGHT'],
      containerRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  return {
    product: 'AMS',
    title: CERTIFICATE_TITLES.AMS,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }
}

registerCertificateBuilder('AMS', buildAmsCertificate)
